export interface Register {
  userName: string;
}

export type FormValues = {
  userName: string;
};

export interface ConfirmRegisterForm {
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  token: string;
  name: string;
  customIndustryName: string;
  countryId: number;
  customerType: number;
  phoneNumber?: string;
  department?: string;
}

export interface ConfirmInviteRequestForm {
  userId: string;
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  token: string;
  name?: string;
  userName?: string;
}

export interface Login {
  userName: string;
  password: string;
}

export interface LoginResult {
  tokenType: string;
  accessToken: string;
}

export interface ForgotPassword {
  userName: string;
}

export interface ResetPasswordModel {
  userName: string;
  token: string;
  newPassword: string;
}

export type ResetPasswordFormValues = {
  userName: string;
  token: string;
  newPassword?: string;
  confirmPassword?: string;
};

export interface User {
  id: string | undefined;
  firstName: string;
  lastName: string;
  role: string;
  phoneNumber?: string;
  companyType?: number;
}

export interface AccountState {
  user?: User;
  accessToken?: string;
}

export class Roles {
  static readonly SYSTEM_ADMINISTRATOR = "SystemAdministrator";
  static readonly ADMIN = "Admin";
  static readonly MANAGER = "Manager";
  static readonly WORKER = "Worker";
  static readonly VIEWER = "Viewer";
  /** Tier-1 QR-kiosk guest pass (B6/SP1). DELIBERATELY excluded from ALL_COMPANY_ROLES
   *  (containment) — a guest is only admitted to the kiosk via PUBLIC_KIOSK_ROLES. */
  static readonly GUEST = "Guest";
  /** Tajmac-ZPS OEM pilot-customer role (MIDAS_PILOT_ROLE_SPEC.md §2/§5.1). Mirrors the
   *  backend `Roles.MIDAS_PILOT` constant EXACTLY — a string mismatch fails silently.
   *  DELIBERATELY INSIDE ALL_COMPANY_ROLES (unlike GUEST): the `/midas` route's
   *  `AuthorizedRoute role={Roles.ALL_COMPANY_ROLES}` — and every backend controller
   *  gating on the same widened constant, incl. the MIDAS proxy + bot callbacks — must
   *  admit a pilot. Confinement is therefore NOT role exclusion but: the fail-closed
   *  `PilotAccess:AllowedCustomerIds` tenant allowlist, the seeded module permissions
   *  (Midas:2 + Machinery:1, everything else 0), and the `PilotRouteGuard` route lock. */
  static readonly MIDAS_PILOT = "MidasPilot";
  static readonly ALL_COMPANY_ROLES = [Roles.ADMIN, Roles.MANAGER, Roles.WORKER, Roles.VIEWER, Roles.MIDAS_PILOT];
  /** Kiosk-v2 route guard: all company roles PLUS the guest pass. Mirrors the backend
   *  PUBLIC_KIOSK_ROLES swap on the 3 public kiosk controllers (SP2).
   *  Because this is DERIVED from ALL_COMPANY_ROLES, MIDAS_PILOT is admitted here too —
   *  accepted, exactly as the backend constant documents (the kiosk routes are
   *  tenant-scoped and PilotRouteGuard gives a pilot no UI path to them). */
  static readonly PUBLIC_KIOSK_ROLES = [...Roles.ALL_COMPANY_ROLES, Roles.GUEST];
  /** Roles that have web app access. Viewer is QR kiosk only — no web app. */
  static readonly WEB_APP_ROLES = [Roles.ADMIN, Roles.MANAGER, Roles.WORKER];
}